<template>
  <div class="game-home" v-loading="isLoading">
    <header class="home-header">
      <div class="brand-block">
        <img src="/Logo.png" alt="ECPM数据" class="brand-logo" />
        <div>
          <div class="brand-eyebrow">ECPM 数据后台</div>
          <h1>游戏总览</h1>
        </div>
      </div>

      <div class="header-actions">
        <div class="user-chip">
          <span class="user-chip-icon"><i class="el-icon-user"></i></span>
          <span>{{ accountLabel }}</span>
        </div>
        <el-button
          v-if="role === '0'"
          type="text"
          icon="el-icon-s-custom"
          @click="$router.push('/account-manage')"
        >伙伴管理</el-button>
        <el-button type="text" icon="el-icon-data-analysis" @click="$router.push('/traffic-master-data')">流量主数据</el-button>
        <el-button type="text" icon="el-icon-switch-button" @click="quit">退出</el-button>
      </div>
    </header>

    <main class="home-content">
      <section class="overview-bar">
        <div class="overview-copy">
          <div class="section-kicker">工作台</div>
          <h2>选择游戏</h2>
          <p>按账号查看游戏，点击卡片进入数据明细。</p>
        </div>
        <div class="all-games-summary">
          <div class="summary-heading">
            <div class="summary-title">
              <span class="summary-kicker">{{ appliedSummaryLabel }}</span>
              <strong>{{ summaryScopeLabel }}</strong>
            </div>
            <div class="summary-controls">
              <el-date-picker
                v-model="summaryDateRange"
                class="summary-date-picker"
                type="daterange"
                size="mini"
                value-format="yyyy-MM-dd"
                format="yyyy-MM-dd"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                :clearable="false"
                :picker-options="summaryPickerOptions"
              ></el-date-picker>
              <el-button
                class="summary-query-button"
                type="primary"
                size="mini"
                icon="el-icon-search"
                :loading="isSummaryLoading"
                @click="loadRangeSummaries"
              >查询</el-button>
              <el-button
                class="summary-privacy-button"
                type="default"
                size="mini"
                :icon="hideGameNames ? 'el-icon-view' : 'el-icon-lock'"
                @click="toggleGameNameVisibility"
              >{{ hideGameNames ? '显示名称' : '隐藏名称' }}</el-button>
            </div>
            <span class="summary-sync-state">
              <i :class="allGamesSummary.completeCount === allGamesSummary.totalCount && allGamesSummary.totalCount ? 'el-icon-circle-check' : 'el-icon-time'"></i>
              {{ aggregateStatus }}
            </span>
          </div>
          <div class="summary-card-grid">
            <div class="summary-card summary-card-ecpm">
              <span>区间 ECPM</span>
              <strong>{{ aggregateMetric('ecpm') }}</strong>
            </div>
            <div class="summary-card summary-card-pre">
              <span>分成前预估收益</span>
              <strong>{{ aggregateMetric('preShareIncome') }}<small v-if="aggregateMetric('preShareIncome') !== '--'">元</small></strong>
            </div>
            <div class="summary-card summary-card-post">
              <span>分成后收益（抖音）</span>
              <strong>{{ aggregateMetric('postShareIncome') }}<small v-if="aggregateMetric('postShareIncome') !== '--' && aggregateMetric('postShareIncome') !== '待同步'">元</small></strong>
            </div>
          </div>
        </div>
        <div class="overview-count">
          <strong>{{ visibleGameCount }}</strong>
          <span>个游戏</span>
        </div>
      </section>

      <div class="filter-bar">
        <el-input
          v-model="accountSearchQuery"
          class="account-search"
          prefix-icon="el-icon-search"
          clearable
          placeholder="搜索账号名称"
        ></el-input>
        <el-input
          v-model="gameSearchQuery"
          class="game-search"
          prefix-icon="el-icon-search"
          clearable
          placeholder="搜索游戏名称"
        ></el-input>
        <el-button
          class="reload-button"
          icon="el-icon-refresh"
          :loading="isLoading"
          @click="fetchGames"
        >刷新列表</el-button>
      </div>

      <section
        v-for="group in groupedGames"
        :key="group.username"
        class="account-section"
      >
        <div class="account-heading">
          <div class="account-heading-main">
            <span class="account-avatar">{{ accountInitial(group.username) }}</span>
            <div>
              <h3>{{ group.username }}</h3>
              <span>{{ group.games.length }} 个游戏</span>
            </div>
          </div>
          <span class="account-hint">点击游戏卡片查看详情</span>
        </div>

        <div class="game-grid">
          <article
            v-for="game in group.games"
            :key="game.gameId || game.gameName"
            class="game-card"
            tabindex="0"
            role="button"
            @click="openGame(game)"
            @keyup.enter="openGame(game)"
          >
            <div class="game-card-head">
              <div class="game-icon">
                <span>{{ gameInitial(hideGameNames ? '' : game.gameName) }}</span>
                <img src="/Logo.png" :alt="hideGameNames ? '游戏图标' : game.gameName" />
              </div>
              <div class="game-title">
                <h4 :title="hideGameNames ? '游戏名称已隐藏' : game.gameName">{{ displayedGameName(game) }}</h4>
                <span class="game-id">游戏 ID {{ game.gameId || '--' }}</span>
              </div>
              <i class="el-icon-arrow-right card-arrow"></i>
            </div>

            <div class="metric-grid">
              <div class="metric-item">
                <span>广告 PV</span>
                <strong>{{ metric(game, 'pv') }}</strong>
              </div>
              <div class="metric-item">
                <span>用户数</span>
                <strong>{{ metric(game, 'userCount') }}</strong>
              </div>
              <div class="metric-item">
                <span>ECPM</span>
                <strong>{{ metric(game, 'ecpm') }}</strong>
              </div>
              <div class="metric-item">
                <span>分成前收益</span>
                <strong class="income-value">{{ metric(game, 'preShareIncome') }}</strong>
              </div>
              <div class="metric-item metric-item-wide">
                <span>分成后收益（抖音）</span>
                <strong class="post-income-value">{{ metric(game, 'postShareIncome') }}</strong>
              </div>
              <div class="metric-item yesterday-income" :title="yesterdayDate + ' 分成前收入（元）'">
                <span>昨日收入</span>
                <strong class="income-value">{{ yesterdayMetric(game, 'preShareIncome') }}</strong>
              </div>
              <div class="metric-item yesterday-post-income" :title="yesterdayDate + ' 抖音官方分成后收入（元）'">
                <span>昨日分成后收入</span>
                <strong class="post-income-value">{{ yesterdayMetric(game, 'postShareIncome') }}</strong>
              </div>
            </div>

            <div class="game-card-foot">
              <span :class="['cache-state', { cached: isCached(game) }]">
                <i :class="isCached(game) ? 'el-icon-circle-check' : 'el-icon-time'"></i>
                {{ cacheStateLabel(game) }}
              </span>
              <span class="detail-link">查看明细 <i class="el-icon-arrow-right"></i></span>
            </div>
          </article>
        </div>
      </section>

      <div v-if="!isLoading && groupedGames.length === 0" class="empty-state">
        <i class="el-icon-folder-opened"></i>
        <h3>{{ hasActiveSearch ? '没有匹配的游戏' : '暂无游戏' }}</h3>
        <p>{{ hasActiveSearch ? '换一个关键词试试。' : '当前账号还没有可查看的游戏。' }}</p>
      </div>
    </main>
  </div>
</template>

<script>
import http from "../api/http";

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function businessYesterday() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(new Date(Date.now() - 24 * 60 * 60 * 1000));
  const value = type => parts.find(part => part.type === type).value;
  return `${value('year')}-${value('month')}-${value('day')}`;
}

export default {
  name: "GameHome",
  data() {
    const today = formatLocalDate(new Date());
    return {
      role: localStorage.getItem("role"),
      isLoading: false,
      isSummaryLoading: false,
      games: [],
      summaries: {},
      rangeSummaries: {},
      yesterdaySummaries: {},
      yesterdayDate: businessYesterday(),
      isYesterdaySummaryLoading: false,
      summaryDateRange: [today, today],
      appliedSummaryDateRange: [today, today],
      hasLoadedRangeSummary: false,
      summaryRefreshTimer: null,
      summaryPickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now();
        }
      },
      accountSearchQuery: "",
      gameSearchQuery: "",
      hideGameNames: localStorage.getItem("ecpm.hideGameNames") === "1"
    };
  },
  computed: {
    hasActiveSearch() {
      return Boolean(this.accountSearchQuery.trim() || this.gameSearchQuery.trim());
    },
    accountLabel() {
      if (this.role === "0") {
        return "管理员";
      }
      return this.groupedGames.length === 1
        ? this.groupedGames[0].username
        : "当前账号";
    },
    groupedGames() {
      const accountKeyword = this.accountSearchQuery.trim().toLowerCase();
      const gameKeyword = this.gameSearchQuery.trim().toLowerCase();
      const groups = Object.create(null);

      this.games.forEach(game => {
        const gameName = game.gameName || "";
        const username = game.username || "当前账号";
        if ((accountKeyword && !username.toLowerCase().includes(accountKeyword)) ||
            (gameKeyword && !gameName.toLowerCase().includes(gameKeyword))) {
          return;
        }

        if (!groups[username]) {
          groups[username] = [];
        }
        groups[username].push(game);
      });

      return Object.keys(groups).sort().map(username => ({
        username,
        games: groups[username].sort((a, b) => (a.gameName || "").localeCompare(b.gameName || ""))
      }));
    },
    filteredGameNames() {
      const names = new Set();
      this.groupedGames.forEach(group => {
        group.games.forEach(game => {
          if (game.gameName) {
            names.add(game.gameName);
          }
        });
      });
      return [...names];
    },
    visibleGameCount() {
      return this.groupedGames.reduce((count, group) => count + group.games.length, 0);
    },
    summaryScopeLabel() {
      return this.hasActiveSearch ? "筛选结果汇总" : "所有游戏汇总";
    },
    summaryGameNames() {
      return [...new Set(this.games.map(game => game.gameName).filter(Boolean))];
    },
    allGamesSummary() {
      const totalCount = this.filteredGameNames.length;
      const loadedSummaries = this.filteredGameNames
        .map(gameName => this.rangeSummaries[gameName])
        .filter(Boolean);
      let pv = 0;
      let preShareIncome = 0;
      let postShareIncome = 0;
      let hasPostShareIncome = loadedSummaries.length === totalCount;

      loadedSummaries.forEach(summary => {
        pv += Number(summary.pv) || 0;
        const preValue = summary.preShareIncome !== undefined && summary.preShareIncome !== null
          ? Number(summary.preShareIncome)
          : Number(summary.income);
        const postValue = Number(summary.postShareIncome);
        preShareIncome += Number.isFinite(preValue) ? preValue : 0;
        if (summary.postShareComplete && summary.postShareIncome !== undefined && summary.postShareIncome !== null && Number.isFinite(postValue)) {
          postShareIncome += postValue;
        } else {
          hasPostShareIncome = false;
        }
      });

      return {
        completeCount: loadedSummaries.filter(summary => summary.cached).length,
        loadedCount: loadedSummaries.length,
        totalCount,
        ready: this.hasLoadedRangeSummary && loadedSummaries.length > 0,
        pv,
        preShareIncome,
        postShareIncome,
        hasPostShareIncome,
        ecpm: pv > 0 ? (preShareIncome / pv) * 1000 : 0
      };
    },
    appliedSummaryLabel() {
      const [startDate, endDate] = this.appliedSummaryDateRange;
      return startDate === endDate ? startDate : `${startDate} 至 ${endDate}`;
    },
    aggregateStatus() {
      const summary = this.allGamesSummary;
      if (this.isSummaryLoading) {
        return "正在汇总";
      }
      if (!summary.totalCount) {
        return this.hasActiveSearch && this.games.length ? "没有匹配的游戏" : "等待游戏列表";
      }
      if (!summary.ready) {
        return "等待查询";
      }
      return summary.completeCount === summary.totalCount
        ? "区间数据已汇总"
        : `今日已同步 ${summary.completeCount}/${summary.totalCount} 个游戏`;
    }
  },
  methods: {
    fetchGames() {
      this.isLoading = true;
      http.get("/api/userGames/getUserGames", {
        headers: {
          "token": `${localStorage.getItem("token")}`
        }
      }).then(res => {
        if (res.data && res.data.code === 1) {
          this.games = Array.isArray(res.data.data) ? res.data.data : [];
          this.loadSummaries();
          this.loadRangeSummaries();
          this.loadYesterdaySummaries();
        } else {
          this.$message.error(res.data?.msg || "获取游戏列表失败");
        }
      }).catch(() => {
        this.$message.error("获取游戏列表失败");
      }).finally(() => {
        this.isLoading = false;
      });
    },
    loadSummaries() {
      const names = this.summaryGameNames;
      if (names.length === 0) {
        this.summaries = {};
        return;
      }

      http.post("/api/ecpm/todaySummaries", names, {
        headers: {
          "token": `${localStorage.getItem("token")}`
        }
      }).then(res => {
        if (!res.data || res.data.code !== 1 || !Array.isArray(res.data.data)) {
          return;
        }

        const summaries = {};
        res.data.data.forEach(summary => {
          summaries[summary.gameName] = summary;
        });
        this.summaries = summaries;
      }).catch(() => {
        // Summary cards can remain as placeholders when Redis is unavailable.
      });
    },
    loadYesterdaySummaries() {
      const date = businessYesterday();
      if (date !== this.yesterdayDate) {
        this.yesterdayDate = date;
        this.yesterdaySummaries = {};
      }
      const names = this.summaryGameNames;
      if (!names.length) {
        this.yesterdaySummaries = {};
        return;
      }
      if (this.isYesterdaySummaryLoading) return;
      this.isYesterdaySummaryLoading = true;
      return http.post('/api/ecpm/rangeSummaries', names, {
        params: { startDate: date, endDate: date },
        headers: { token: `${localStorage.getItem('token')}` }
      }).then(res => {
        if (date !== this.yesterdayDate || date !== businessYesterday()) return;
        if (!res.data || res.data.code !== 1 || !Array.isArray(res.data.data)) return;
        const summaries = {};
        res.data.data.forEach(summary => { summaries[summary.gameName] = summary; });
        this.yesterdaySummaries = summaries;
      }).catch(() => {
        // Retain the last successful snapshot for this date during transient errors.
      }).finally(() => {
        this.isYesterdaySummaryLoading = false;
      });
    },
    yesterdayMetric(game, key) {
      const summary = this.yesterdaySummaries[game.gameName];
      if (!summary || (key === 'postShareIncome' && !summary.postShareComplete)) return '待同步';
      const raw = summary[key];
      if (raw === undefined || raw === null || raw === '') return '待同步';
      const value = Number(raw);
      return Number.isFinite(value) ? value.toFixed(2) : '待同步';
    },
    loadRangeSummaries(silent = false) {
      if (!Array.isArray(this.summaryDateRange) || this.summaryDateRange.length !== 2) {
        if (!silent) {
          this.$message.warning("请选择汇总日期范围");
        }
        return;
      }

      const names = this.summaryGameNames;
      if (names.length === 0) {
        this.rangeSummaries = {};
        this.hasLoadedRangeSummary = false;
        return;
      }

      const [startDate, endDate] = this.summaryDateRange;
      this.isSummaryLoading = true;
      http.post("/api/ecpm/rangeSummaries", names, {
        params: { startDate, endDate },
        headers: {
          "token": `${localStorage.getItem("token")}`
        }
      }).then(res => {
        if (!res.data || res.data.code !== 1 || !Array.isArray(res.data.data)) {
          if (!silent) {
            this.$message.error(res.data?.msg || "汇总数据加载失败");
          }
          return;
        }

        const summaries = {};
        res.data.data.forEach(summary => {
          summaries[summary.gameName] = summary;
        });
        this.rangeSummaries = summaries;
        this.appliedSummaryDateRange = [startDate, endDate];
        this.hasLoadedRangeSummary = true;
      }).catch(() => {
        if (!silent) {
          this.$message.error("汇总数据加载失败");
        }
      }).finally(() => {
        this.isSummaryLoading = false;
      });
    },
    refreshSummariesSilently() {
      if (this.isLoading || this.summaryGameNames.length === 0) {
        return;
      }
      this.loadYesterdaySummaries();
      if (this.isSummaryLoading) return;
      this.loadSummaries();
      const today = formatLocalDate(new Date());
      const [startDate, endDate] = this.summaryDateRange || [];
      if (startDate && endDate && endDate <= today) {
        this.loadRangeSummaries(true);
      }
    },
    startSummaryAutoRefresh() {
      this.stopSummaryAutoRefresh();
      this.summaryRefreshTimer = window.setInterval(() => {
        this.refreshSummariesSilently();
      }, 15000);
    },
    stopSummaryAutoRefresh() {
      if (this.summaryRefreshTimer) {
        window.clearInterval(this.summaryRefreshTimer);
        this.summaryRefreshTimer = null;
      }
    },
    summaryFor(game) {
      return this.summaries[game.gameName] || null;
    },
    rangeSummaryFor(game) {
      return this.rangeSummaries[game.gameName] || null;
    },
    isCached(game) {
      const summary = this.hasLoadedRangeSummary ? this.rangeSummaryFor(game) : this.summaryFor(game);
      return Boolean(summary && summary.cached);
    },
    cacheStateLabel(game) {
      if (!this.hasLoadedRangeSummary) {
        return this.isCached(game) ? "今日数据已同步" : "等待数据缓存";
      }
      const summary = this.rangeSummaryFor(game);
      if (!summary) {
        return "区间数据未返回";
      }
      return summary.cached ? "区间数据已加载" : "今日数据未同步，区间可能不完整";
    },
    metric(game, key) {
      const isRangeMetric = this.hasLoadedRangeSummary;
      const summary = isRangeMetric ? this.rangeSummaryFor(game) : this.summaryFor(game);
      if (key === "postShareIncome" && (!summary || !summary.postShareComplete || summary.postShareIncome == null)) return "待同步";
      if (!summary || (!isRangeMetric && !summary.cached && key !== "postShareIncome")) {
        return "--";
      }
      const rawValue = key === "preShareIncome"
        && (summary[key] === undefined || summary[key] === null)
        ? summary.income
        : summary[key];
      if (key === "ecpm" || key === "income" || key === "preShareIncome" || key === "postShareIncome") {
        if (rawValue === undefined || rawValue === null) {
          return "--";
        }
        const value = Number(rawValue);
        return Number.isFinite(value) ? value.toFixed(2) : "0.00";
      }
      return rawValue === undefined || rawValue === null ? "0" : rawValue;
    },
    displayedGameName(game) {
      return this.hideGameNames ? "******" : (game.gameName || "未命名游戏");
    },
    toggleGameNameVisibility() {
      this.hideGameNames = !this.hideGameNames;
      localStorage.setItem("ecpm.hideGameNames", this.hideGameNames ? "1" : "0");
    },
    aggregateMetric(key) {
      const summary = this.allGamesSummary;
      if (key === "postShareIncome" && (!summary.ready || !summary.hasPostShareIncome)) return "待同步";
      if (!summary.ready) {
        return "--";
      }
      const value = Number(summary[key]);
      return Number.isFinite(value) ? value.toFixed(2) : "0.00";
    },
    gameInitial(name) {
      return (name || "游").slice(0, 1);
    },
    accountInitial(name) {
      return (name || "账").slice(0, 1).toUpperCase();
    },
    openGame(game) {
      this.$router.push({
        name: "GameDetail",
        query: { game: game.gameName }
      });
    },
    quit() {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      this.$router.push("/login");
    }
  },
  mounted() {
    this.fetchGames();
    this.startSummaryAutoRefresh();
  },
  beforeDestroy() {
    this.stopSummaryAutoRefresh();
  }
};
</script>

<style scoped>
.game-home {
  min-height: 100vh;
  color: #172337;
  background: #f3f6fa;
}

.home-header {
  min-height: 76px;
  padding: 0 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #e4eaf1;
}

.brand-block,
.header-actions,
.account-heading-main,
.game-card-head,
.game-card-foot {
  display: flex;
  align-items: center;
}

.brand-block {
  gap: 14px;
}

.brand-logo {
  width: 108px;
  height: 30px;
  object-fit: contain;
  object-position: left center;
}

.brand-eyebrow,
.section-kicker {
  color: #7d8ba0;
  font-size: 11px;
  line-height: 1.2;
  text-transform: uppercase;
}

.brand-block h1 {
  margin: 3px 0 0;
  color: #172337;
  font-size: 19px;
  font-weight: 650;
}

.header-actions {
  gap: 18px;
}

.user-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #536277;
  font-size: 13px;
}

.user-chip-icon,
.account-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: #2f70c9;
}

.user-chip-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}

.home-content {
  width: min(1480px, calc(100% - 64px));
  margin: 0 auto;
  padding: 34px 0 56px;
}

.overview-bar {
  display: flex;
  align-items: stretch;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 22px;
}

.overview-copy {
  flex: 0 0 auto;
  align-self: flex-end;
}

.overview-bar h2 {
  margin: 6px 0 6px;
  color: #172337;
  font-size: 28px;
  font-weight: 650;
}

.overview-bar p {
  margin: 0;
  color: #8190a5;
  font-size: 13px;
}

.all-games-summary {
  min-width: 0;
  flex: 1 1 auto;
  align-self: flex-end;
}

.summary-heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.summary-heading > div {
  min-width: 0;
}

.summary-title {
  flex: 0 0 auto;
}

.summary-controls {
  display: flex;
  align-items: center;
  gap: 7px;
}

.summary-date-picker {
  width: 250px !important;
}

.summary-query-button {
  flex: 0 0 auto;
}

.summary-privacy-button {
  flex: 0 0 auto;
}

.summary-kicker {
  display: block;
  margin-bottom: 2px;
  color: #8492a5;
  font-size: 10px;
  line-height: 1.2;
  text-transform: uppercase;
}

.summary-heading strong {
  display: block;
  overflow: hidden;
  color: #40526a;
  font-size: 13px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-sync-state {
  overflow: hidden;
  margin-left: auto;
  color: #8a98aa;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-sync-state i {
  margin-right: 4px;
}

.summary-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.summary-card {
  min-width: 0;
  min-height: 78px;
  padding: 11px 13px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 8px;
  color: #ffffff;
  box-shadow: 0 5px 14px rgba(45, 86, 132, 0.14);
}

.summary-card-ecpm {
  background: linear-gradient(135deg, #4e82d1 0%, #5363b3 100%);
}

.summary-card-pre {
  background: linear-gradient(135deg, #2f91a0 0%, #3f6fbe 100%);
}

.summary-card-post {
  background: linear-gradient(135deg, #3c9b7b 0%, #5369aa 100%);
}

.summary-card span {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.88);
  font-size: 11px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-card strong {
  display: block;
  overflow: hidden;
  margin-top: 5px;
  font-size: 23px;
  font-weight: 700;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-card small {
  margin-left: 3px;
  font-size: 12px;
  font-weight: 500;
}

.overview-count {
  align-self: flex-end;
  min-width: 112px;
  padding: 13px 18px;
  text-align: right;
  background: #ffffff;
  border: 1px solid #e2e9f1;
  border-radius: 8px;
}

.overview-count strong {
  display: block;
  color: #2467bd;
  font-size: 26px;
  line-height: 1;
}

.overview-count span {
  display: block;
  margin-top: 6px;
  color: #8a98aa;
  font-size: 12px;
}

.filter-bar {
  display: grid;
  grid-template-columns: minmax(180px, 240px) minmax(220px, 280px) auto;
  align-items: center;
  gap: 10px;
  margin-bottom: 30px;
}

.account-search,
.game-search {
  width: 100%;
}

.reload-button {
  color: #536277;
  border-color: #d8e1eb;
  background: #ffffff;
}

.account-section {
  margin-bottom: 34px;
}

.account-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #dfe7ef;
}

.account-heading-main {
  gap: 10px;
}

.account-avatar {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 650;
}

.account-heading h3 {
  margin: 0 0 3px;
  color: #26364d;
  font-size: 15px;
  font-weight: 650;
}

.account-heading-main span:not(.account-avatar),
.account-hint {
  color: #8a98aa;
  font-size: 12px;
}

.account-hint {
  color: #9aa8b8;
}

.game-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(286px, 1fr));
  gap: 16px;
}

.game-card {
  min-width: 0;
  padding: 18px 18px 14px;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #e0e7ef;
  border-radius: 8px;
  box-shadow: 0 3px 12px rgba(27, 57, 91, 0.04);
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.game-card:hover,
.game-card:focus {
  outline: none;
  border-color: #8eb9e8;
  box-shadow: 0 8px 22px rgba(36, 103, 189, 0.12);
  transform: translateY(-2px);
}

.game-card-head {
  min-width: 0;
  gap: 12px;
  padding-bottom: 17px;
}

.game-icon {
  position: relative;
  flex: 0 0 58px;
  width: 58px;
  height: 58px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: #101a2c;
  border-radius: 10px;
}

.game-icon > span {
  position: absolute;
  font-size: 22px;
  font-weight: 700;
}

.game-icon img {
  position: absolute;
  left: 0;
  top: 0;
  width: 208px;
  height: 58px;
  max-width: none;
  object-fit: contain;
  object-position: left center;
}

.game-title {
  min-width: 0;
  flex: 1;
}

.game-title h4 {
  overflow: hidden;
  margin: 0 0 7px;
  color: #24344b;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.game-id {
  color: #9aa7b7;
  font-size: 11px;
}

.card-arrow {
  flex: 0 0 auto;
  color: #aebaca;
  font-size: 18px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  border-top: 1px solid #edf1f5;
  border-bottom: 1px solid #edf1f5;
}

.metric-item {
  min-width: 0;
  padding: 12px 10px 11px 0;
}

.metric-item:nth-child(2),
.metric-item:nth-child(4),
.metric-item.yesterday-post-income {
  padding-left: 14px;
  border-left: 1px solid #edf1f5;
}

.metric-item:nth-child(n + 3) {
  border-top: 1px solid #edf1f5;
}

.metric-item span {
  display: block;
  margin-bottom: 5px;
  overflow: hidden;
  color: #8b99aa;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-item strong {
  display: block;
  overflow: hidden;
  color: #2d72ca;
  font-size: 17px;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-item .income-value {
  color: #e89927;
}

.metric-item.metric-item-wide {
  grid-column: 1 / -1;
  padding-left: 0;
  border-left: 0;
}

.metric-item .post-income-value {
  color: #3b9b78;
}

.game-card-foot {
  justify-content: space-between;
  gap: 8px;
  padding-top: 13px;
}

.cache-state,
.detail-link {
  overflow: hidden;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cache-state {
  color: #9aa7b7;
}

.cache-state.cached {
  color: #52a77a;
}

.cache-state i {
  margin-right: 4px;
}

.detail-link {
  flex: 0 0 auto;
  color: #2f70c9;
  font-weight: 600;
}

.empty-state {
  padding: 90px 20px;
  text-align: center;
  color: #8492a5;
  background: #ffffff;
  border: 1px dashed #d6e0ea;
  border-radius: 8px;
}

.empty-state i {
  color: #b8c5d4;
  font-size: 38px;
}

.empty-state h3 {
  margin: 15px 0 7px;
  color: #4a5b71;
  font-size: 16px;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
}

@media (max-width: 760px) {
  .home-header {
    min-height: auto;
    padding: 16px 18px;
    align-items: flex-start;
    gap: 14px;
  }

  .brand-logo {
    width: 82px;
    height: 26px;
  }

  .header-actions {
    gap: 5px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .user-chip {
    display: none;
  }

  .home-content {
    width: calc(100% - 32px);
    padding-top: 24px;
  }

  .overview-bar {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 16px;
  }

  .overview-copy {
    flex: 1 1 auto;
  }

  .overview-bar h2 {
    font-size: 24px;
  }

  .overview-count {
    min-width: 82px;
    padding: 11px 12px;
  }

  .all-games-summary {
    flex: 1 1 100%;
    order: 3;
  }

  .summary-heading {
    align-items: flex-start;
  }

  .summary-title,
  .summary-controls,
  .summary-date-picker {
    width: 100% !important;
  }

  .summary-controls {
    align-items: stretch;
    flex-wrap: wrap;
  }

  .summary-date-picker {
    flex: 1 1 100%;
  }

  .summary-controls .el-button {
    flex: 1 1 0;
  }

  .summary-sync-state {
    width: 100%;
    margin-left: 0;
  }

  .summary-card-grid {
    grid-template-columns: 1fr;
  }

  .filter-bar {
    grid-template-columns: minmax(0, 1fr);
  }

  .account-search,
  .game-search {
    width: 100%;
  }

  .reload-button {
    width: 100%;
    padding-left: 12px;
    padding-right: 12px;
  }

  .account-hint {
    display: none;
  }

  .game-grid {
    grid-template-columns: 1fr;
  }
}
</style>
